import React, {forwardRef, useImperativeHandle, useState, useEffect, useRef} from 'react';
import { AiOutlineEye, AiOutlineEyeInvisible, AiOutlineNotification, AiOutlineDownload } from 'react-icons/ai';
import { NotificationsStore } from '../stores/notifications/notificationsStore';
import * as htmlToImage from 'html-to-image';
import { RatingStore } from '../stores/rating/ratingStore';
import { TeamStore } from '../stores/team/teamStore';
import { observer } from 'mobx-react-lite';

export const Day = 'Day';
export const Week = 'Week';
export const Month = 'Month';
export const Agenda = 'Agenda';
export const Resize = {};
export const DragAndDrop = {};

export const ViewsDirective: React.FC<{ children?: React.ReactNode }> = ({children}) => {
  // placeholder to keep API shape
  return <>{children}</>;
};

export const ViewDirective: React.FC<{ option?: any }> = () => {
  return null;
};

export const Inject = ({ services }: { services?: any[] }) => null as any;

export type EventItem = { StartTime?: string | Date; EndTime?: string | Date; Subject?: string; [k: string]: any };

export const ScheduleComponent = observer(forwardRef((props: any, ref) => {
  const { height, selectedDate: selectedDateProp, eventSettings = {}, dragStart, views, currentView } = props;
  const [selectedDate, setSelectedDate] = useState<Date>(selectedDateProp ? new Date(selectedDateProp) : new Date());
  const [events, setEvents] = useState<EventItem[]>(eventSettings.dataSource || []);
  const [view, setView] = useState<string>(currentView || (views && views[0]) || 'Agenda');

  useEffect(() => {
    setEvents(eventSettings.dataSource || []);
  }, [eventSettings.dataSource]);

  useEffect(() => {
    if (selectedDateProp) setSelectedDate(new Date(selectedDateProp));
  }, [selectedDateProp]);

  const selectedDateRef = useRef<Date>(selectedDate);
  const viewRef = useRef<string>(view);
  const instanceRef = useRef<any>(null);
  const [selectedEvent, setSelectedEvent] = useState<any | null>(null);
  const [showValue, setShowValue] = useState(true);
  const [loadingNotify, setLoadingNotify] = useState(false);
  const [notifyTitle, setNotifyTitle] = useState("Agendamento Confirmado");
  const [notifyMsg, setNotifyMsg] = useState("");
  const [isExportingTicket, setIsExportingTicket] = useState(false);

  useEffect(() => {
    if (selectedEvent) {
      setNotifyTitle("Agendamento Confirmado");
      setNotifyMsg("Sua solicitação de serviço foi agendada com sucesso. Agora é só aguardar nossas especialistas na data marcada.");
    }
  }, [selectedEvent]);

  const handleSendNotification = async (ev: any) => {
    if (!ev) return;
    const userId = ev.userId || ev.original?.userId;
    const fcmId = ev.fcmId || ev.original?.fcmId;

    if (!userId || !fcmId) {
      alert("Usuário sem identificação para notificação (FCM ID ausente).");
      return;
    }

    if (!notifyTitle.trim() || !notifyMsg.trim()) {
      alert("Por favor, preencha o título e a mensagem da notificação.");
      return;
    }

    setLoadingNotify(true);
    try {
      await NotificationsStore().pushToUser(userId, fcmId, notifyTitle, notifyMsg);
      alert("Notificação enviada com sucesso!");
    } catch (err) {
      console.error(err);
      alert("Falha ao enviar notificação.");
    } finally {
      setLoadingNotify(false);
    }
  };

  const handleDownloadTicket = async () => {
    setIsExportingTicket(true);
    setTimeout(async () => {
      const node = document.getElementById('ticket-modal-content');
      if (!node) {
        setIsExportingTicket(false);
        return;
      }
      try {
        const dataUrl = await htmlToImage.toPng(node, { 
          quality: 1.0, 
          backgroundColor: '#ffffff',
          cacheBust: true,
          pixelRatio: 2,
          filter: (el: any) => {
            if (el && el.hasAttribute && el.hasAttribute('data-ticket-ignore')) return false;
            return true;
          }
        });
        
        // Converte dataUrl base64 gigante para Blob para evitar os limites do download de URL ou de nova aba
        const response = await fetch(dataUrl);
        const blob = await response.blob();
        const objectUrl = URL.createObjectURL(blob);
        
        const link = document.createElement('a');
        link.download = `ticket-limpeza-${selectedEvent?.Id || 'avulso'}.png`;
        link.href = objectUrl;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        // Limpeza de memória
        setTimeout(() => URL.revokeObjectURL(objectUrl), 3000);
        
      } catch (err) {
        console.error('Erro ao gerar ticket', err);
        alert('Não foi possível gerar a imagem.');
      } finally {
        setIsExportingTicket(false);
      }
    }, 150);
  };

  useEffect(() => { selectedDateRef.current = selectedDate; }, [selectedDate]);
  useEffect(() => { viewRef.current = view; }, [view]);

  if (!instanceRef.current) {
    instanceRef.current = {
      get selectedDate() { return selectedDateRef.current; },
      set selectedDate(v: Date) { selectedDateRef.current = new Date(v); setSelectedDate(new Date(v)); },
      dataBind: () => { /* no-op */ },
      next: () => {
        const v = viewRef.current;
        if (v === 'Day') setSelectedDate(d => new Date(d.getTime() + 24 * 3600 * 1000));
        else if (v === 'Week') setSelectedDate(d => new Date(d.getTime() + 7 * 24 * 3600 * 1000));
        else setSelectedDate(d => new Date(d.getFullYear(), d.getMonth() + 1, 1));
      },
      previous: () => {
        const v = viewRef.current;
        if (v === 'Day') setSelectedDate(d => new Date(d.getTime() - 24 * 3600 * 1000));
        else if (v === 'Week') setSelectedDate(d => new Date(d.getTime() - 7 * 24 * 3600 * 1000));
        else setSelectedDate(d => new Date(d.getFullYear(), d.getMonth() - 1, 1));
      },
      setView: (v: string) => { setView(v); }
    };
  }

  useImperativeHandle(ref, () => instanceRef.current, []);

  const _convertPlan = (plan: string) => {
      switch(plan) {
        case 'card':
          return "Cartão de crédito";
        default:
          return "Pix";
      }
  }

  const getPlanDetails = (plan: string) => {
    const p = (plan || '').toUpperCase();
    
    const studio = [
      "Cozinha executiva",
      "Desinfecção hospitalar do banheiro",
      "Retirada de lixos",
      "Arrumação de cama padrão hotelaria",
      "Higienização UV - colchão",
      "Organização de superfícies",
      "Pó em moveis",
      "Limpeza de vidros de sacada",
      "Aspiração e mopagem de piso"
    ];

    const light = [
      "Aspiração e mopagem de piso",
      "Limpeza de superfícies externas",
      "Desinfecção hospitalar de banheiro e cozinha (pias e sanitários)",
      "Arrumação de cama padrão hotelaria"
    ];

    const regular = [
      "Aspiração e mopagem de piso",
      "Limpeza de superfícies externas",
      "Desinfecção hospitalar de banheiro e cozinha (pias e sanitários)",
      "Arrumação de cama padrão hotelaria",
      "Limpeza de tapetes e debaixo das camas",
      "Limpeza de box de banheiro (remoção de manchas de gordura corporal)",
      "Desinfecção Hospitalar de superfícies de contato (maçanetas, interruptores, metais)",
      "Troca de roupas de cama e banho"
    ];

    const deep = [
      "Aspiração e mopagem de piso",
      "Limpeza de superfícies externas",
      "Desinfecção hospitalar de banheiro e cozinha (pias e sanitários)",
      "Arrumação de cama padrão hotelaria",
      "Limpeza de tapetes e debaixo das camas",
      "Limpeza de box de banheiro (remoção de manchas de gordura corporal)",
      "Desinfecção Hospitalar de superfícies de contato (maçanetas, interruptores, metais)",
      "Troca de roupas de cama e banho",
      "Limpeza interna de fogão, exaustor, micro-ondas e geladeira",
      "Limpeza de vidros internos e rodapés",
      "Aspiração detalhada de frestas e trilhos"
    ];

    if (p === 'STUDIO') return { items: studio, time: null };
    if (p === 'LIGHT' || p === 'LEVE') return { items: light, time: "45 a 60 minutos" };
    if (p === 'REGULAR' || p === 'PADRÃO' || p === 'PADRAO') return { items: regular, time: "90 minutos" };
    if (p === 'DEEP' || p === 'PESADA') return { items: deep, time: "120 a 160 minutos" };
    
    return null;
  };

  // helper functions removed (unused) - parsing happens in parsedEvents

  const parseEvent = (ev: any) => {
    try {
      const s = ev?.StartTime ? new Date(ev.StartTime) : null;
      const e = ev?.EndTime ? new Date(ev.EndTime) : null;
      return { original: ev, start: s && !isNaN(s.getTime()) ? s : null, end: e && !isNaN(e.getTime()) ? e : null };
    } catch (err) {
      return { original: ev, start: null, end: null };
    }
  };

  const parsedEvents = events.map(parseEvent).filter(pe => {
    const status = (pe.original?.Status || pe.original?.status || '').toString().toUpperCase();
    return status !== 'CANCELED';
  });

  const firstDayOfWeek = props.firstDayOfWeek ?? 0; // 0=Sunday, 1=Monday

  const formatKey = (d: Date) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  const parseRecurrence = (rule: string | undefined) => {
    if (!rule) return null;
    const parts = rule.split(';').map(p => p.trim()).filter(Boolean);
    const out: any = {};
    parts.forEach(p => {
      const [k, v] = p.split('=');
      if (!k) return;
      out[k.toUpperCase()] = v;
    });
    return out;
  };

  const renderSubject = (subject: any) => {
    const raw = subject ?? '';
    const text = String(raw).replace(/<\/?br\s*\/?\s*>/gi, '\n');
    return <div style={{whiteSpace: 'pre-wrap'}}>{text}</div>;
  };

  const occursOnDate = (ev: any, dt: Date) => {
    if (!ev.start) return false;
    const rule = parseRecurrence(ev.original?.RecurrenceRule);
    const evStart = ev.start;
    const targetKey = formatKey(dt);
    const evKey = formatKey(evStart);
    // exact date match
    if (!rule) return evKey === targetKey;

    const freq = (rule.FREQ || '').toUpperCase();
    const interval = Number(rule.INTERVAL || '1') || 1;
    // compute days/weeks between
    const startOfEv = new Date(evStart.getFullYear(), evStart.getMonth(), evStart.getDate());
    const startOfDt = new Date(dt.getFullYear(), dt.getMonth(), dt.getDate());
    const diffMs = startOfDt.getTime() - startOfEv.getTime();
    const diffDays = Math.round(diffMs / (24 * 3600 * 1000));

    if (freq === 'DAILY') {
      if (diffDays < 0) return false;
      return diffDays % interval === 0;
    }

    if (freq === 'WEEKLY') {
      if (diffDays < 0) return false;
      const diffWeeks = Math.round(diffDays / 7);
      if (rule.BYDAY) {
        const by = (rule.BYDAY || '').split(',').map((b: string) => b.trim().toUpperCase());
        const wkdayMap: Record<number,string> = {0: 'SU',1:'MO',2:'TU',3:'WE',4:'TH',5:'FR',6:'SA'};
        const dayCode = wkdayMap[startOfDt.getDay()];
        if (!by.includes(dayCode)) return false;
        // find first occurrence on or after evStart that matches this BYDAY
        // compute first occurrence date for this dayCode
        const targetNum = Object.keys(wkdayMap).find((k) => wkdayMap[Number(k)] === dayCode);
        const targetWeekday = targetNum ? Number(targetNum) : startOfEv.getDay();
        // search for the first date >= evStart that has weekday === targetWeekday
        let firstOcc = new Date(startOfEv.getFullYear(), startOfEv.getMonth(), startOfEv.getDate());
        for (let i=0;i<7;i++) {
          if (firstOcc.getDay() === targetWeekday) break;
          firstOcc.setDate(firstOcc.getDate() + 1);
        }
        const diffMsFirst = startOfDt.getTime() - firstOcc.getTime();
        const diffWeeksFirst = Math.round(diffMsFirst / (7 * 24 * 3600 * 1000));
        if (diffWeeksFirst < 0) return false;
        return diffWeeksFirst % interval === 0;
      }
      // fallback: match weekday of original start
      const evWeekday = startOfEv.getDay();
      if (evWeekday !== startOfDt.getDay()) return false;
      return diffWeeks % interval === 0;
    }

    return false;
  };

  const occurrencesForRange = (ev: any, rangeStart: Date, rangeEnd: Date) => {
    const occ: any[] = [];
    if (!ev.start) return occ;
    const rule = parseRecurrence(ev.original?.RecurrenceRule);
    // If no recurrence, just check single occurrence
    if (!rule) {
      const evDateKey = formatKey(ev.start);
      if (formatKey(rangeStart) <= evDateKey && evDateKey <= formatKey(rangeEnd)) {
        occ.push({ 
          ...ev.original, 
          StartTime: ev.start.toISOString(), 
          EndTime: ev.end ? ev.end.toISOString() : null,
          isFirst: ev.original?.isAbsoluteFirst 
        });
      }
      return occ;
    }
    // iterate each day in range and test
    for (let d = new Date(rangeStart.getFullYear(), rangeStart.getMonth(), rangeStart.getDate()); d <= rangeEnd; d.setDate(d.getDate()+1)) {
      const dt = new Date(d);
      if (occursOnDate(ev, dt)) {
        // build occurrence start/end using time component from original
        const start = new Date(dt.getFullYear(), dt.getMonth(), dt.getDate(), ev.start.getHours(), ev.start.getMinutes(), ev.start.getSeconds());
        const end = ev.end ? new Date(dt.getFullYear(), dt.getMonth(), dt.getDate(), ev.end.getHours(), ev.end.getMinutes(), ev.end.getSeconds()) : new Date(start.getTime() + 3600*1000);
        
        // Check if this is the first occurrence (same date as ev.start)
        const isFirst = ev.original?.isAbsoluteFirst && formatKey(dt) === formatKey(ev.start);
        
        occ.push({ ...ev.original, StartTime: start.toISOString(), EndTime: end.toISOString(), isFirst });
      }
    }
    return occ;
  };

  const viewLabels: Record<string, string> = {
    'Day': 'Dia',
    'Week': 'Semana',
    'Month': 'Mês',
    'Agenda': 'Agenda'
  };

  const renderHeader = () => (
    <div className="border-b border-gray-200 dark:border-gray-700 text-gray-900 dark:text-gray-200" style={{padding: 12, display: 'flex', gap: 8, alignItems: 'center'}}>
      <button type="button" onClick={() => setSelectedDate(new Date())}>&lt;&lt;</button>
      <button type="button" onClick={() => instanceRef.current && instanceRef.current.previous()}>Anterior</button>
      <div style={{flex: 1, textAlign: 'center'}}><strong>{viewLabels[view] || view}</strong> — {selectedDate.toLocaleDateString()}</div>
      <button type="button" onClick={() => instanceRef.current && instanceRef.current.next()}>Próximo</button>
      {views && (
        <select value={view} onChange={(e) => setView(e.target.value)} className="border dark:border-gray-600 p-1 rounded bg-white dark:bg-[#33373E] dark:text-gray-200 focus:outline-none">
          {views.map((v: string) => <option key={v} value={v}>{viewLabels[v] || v}</option>)}
        </select>
      )}
    </div>
  );

  const renderMonth = () => {
    const y = selectedDate.getFullYear();
    const m = selectedDate.getMonth();
    const first = new Date(y, m, 1);
    const startOffset = (first.getDay() - firstDayOfWeek + 7) % 7;
    const gridStart = new Date(y, m, 1 - startOffset);
    const weeks: Date[][] = [];
    for (let w = 0; w < 6; w++) {
      const week: Date[] = [];
      for (let d = 0; d < 7; d++) {
        const dt = new Date(gridStart.getFullYear(), gridStart.getMonth(), gridStart.getDate() + w * 7 + d);
        week.push(dt);
      }
      weeks.push(week);
    }

    // compute occurrences for the full grid range (6 weeks)
    const gridEnd = new Date(gridStart.getFullYear(), gridStart.getMonth(), gridStart.getDate() + 6 * 7 - 1);
    const occurrences: any[] = [];
    parsedEvents.forEach(pe => {
      const occs = occurrencesForRange(pe, gridStart, gridEnd);
      occs.forEach(o => occurrences.push(o));
    });

    const eventsByDate = new Map<string, any[]>();
    occurrences.forEach(o => {
      if (!o?.StartTime) return;
      const key = formatKey(new Date(o.StartTime));
      if (!eventsByDate.has(key)) eventsByDate.set(key, []);
      eventsByDate.get(key)!.push(o);
    });

    return (
      <div style={{display: 'grid', gap: 8}}>
        <div style={{display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', textAlign: 'center', fontWeight: 600}}>
          {(() => {
            const names = ['Dom','Seg','Ter','Qua','Qui','Sex','Sáb'];
            const shifted = [];
            for (let i = 0; i < 7; i++) shifted.push(names[(i + firstDayOfWeek) % 7]);
            return shifted.map((d, i) => <div key={i}>{d}</div>);
          })()}
        </div>
        <div style={{display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 8}}>
          {weeks.flat().map((dt, idx) => {
            const key = formatKey(dt);
            const dayEvents = eventsByDate.get(key) || [];
            const isCurrMonth = dt.getMonth() === m;
            return (
              <div key={idx} className={isCurrMonth ? 'bg-white dark:bg-[#33373E] border border-gray-200 dark:border-gray-700' : 'bg-gray-50 dark:bg-[#20232A] border border-gray-200 dark:border-gray-700'} style={{minHeight: 100, padding: 6}}>
                <div className="text-gray-600 dark:text-gray-400" style={{fontSize: 12}}>{dt.getDate()}</div>
                <div style={{marginTop: 6}}>
                  {dayEvents.slice(0,3).map((ev: any, i: number) => {
                    const isFirst = ev.isFirst;
                    const isDelivered = (ev.Status || ev.status || ev.original?.Status || ev.original?.status || '').toString().toUpperCase() === 'DELIVERED';
                    return (
                      <div
                        key={i}
                        role="button"
                        tabIndex={0}
                        onKeyDown={() => {}}
                        onClick={() => setSelectedEvent(ev)}
                        className={`cursor-pointer text-xs p-1 rounded-md mb-1 ${
                          isDelivered 
                            ? 'bg-green-600 text-white' 
                            : isFirst 
                              ? 'bg-orange-500 text-white' 
                              : 'bg-[#e6f4ea] text-[#1f4f2c] dark:bg-[#1E3A2A] dark:text-[#A7F3D0]'
                        }`}
                      >
                        {renderSubject(ev.Subject || ev.subject)}
                      </div>
                    );
                  })}
                  {dayEvents.length > 3 && <div style={{fontSize: 11, color: '#888'}}>+{dayEvents.length-3} mais</div>}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderWeek = () => {
    const sd = selectedDate;
    const start = new Date(sd.getFullYear(), sd.getMonth(), sd.getDate());
    const startDay = (start.getDay() - firstDayOfWeek + 7) % 7;
    const weekStart = new Date(start.getFullYear(), start.getMonth(), start.getDate() - startDay);
    const days: Date[] = [];
    for (let i=0;i<7;i++) days.push(new Date(weekStart.getFullYear(), weekStart.getMonth(), weekStart.getDate()+i));

    const weekEnd = new Date(weekStart.getFullYear(), weekStart.getMonth(), weekStart.getDate() + 6);
    const occurrences: any[] = [];
    parsedEvents.forEach(pe => {
      const occs = occurrencesForRange(pe, weekStart, weekEnd);
      occs.forEach(o => occurrences.push(o));
    });

    const eventsByDate = new Map<string, any[]>();
    occurrences.forEach(o => {
      if (!o?.StartTime) return;
      const key = formatKey(new Date(o.StartTime));
      if (!eventsByDate.has(key)) eventsByDate.set(key, []);
      eventsByDate.get(key)!.push(o);
    });

    return (
      <div style={{display: 'flex', flexDirection: 'column', gap: 8}}>
        {days.map((dt, idx) => {
          const key = formatKey(dt);
          const dayEvents = (eventsByDate.get(key) || []).sort((a:any,b:any)=> new Date(a.StartTime).getTime() - new Date(b.StartTime).getTime());
          return (
            <div key={idx} className="border border-gray-200 dark:border-gray-700" style={{padding: 8, minHeight: 120}}>
              <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:6}}>
                <div className="text-gray-900 dark:text-gray-200" style={{fontWeight: 700}}>{dt.toLocaleDateString(undefined, {weekday: 'long'})}</div>
                <div className="text-gray-500 dark:text-gray-400" style={{fontSize:12}}>{dt.toLocaleDateString(undefined, {month: 'short', day: 'numeric'})}</div>
              </div>
              {dayEvents.length === 0 ? <div style={{color:'#888'}}>Sem eventos</div> : dayEvents.map((ev:any, i:number) => {
                const isFirst = ev.isFirst;
                const isDelivered = (ev.Status || ev.status || ev.original?.Status || ev.original?.status || '').toString().toUpperCase() === 'DELIVERED';
                return (
                  <div
                    key={i}
                    role="button"
                    tabIndex={0}
                    onKeyDown={() => {}}
                    onClick={() => setSelectedEvent(ev)}
                    style={{
                      cursor:'pointer', 
                      padding:6, 
                      borderRadius:4, 
                      background: isDelivered ? '#28a745' : (isFirst ? '#ff751f' : '#eef6ff'), 
                      color: (isDelivered || isFirst) ? '#fff' : 'inherit',
                      marginBottom:6
                    }}
                  >
                    <div style={{fontSize:13, fontWeight:600}}>{renderSubject(ev.Subject || ev.subject)}</div>
                    <div style={{fontSize:12, color: (isDelivered || isFirst) ? '#eee' : '646464ff'}}>{new Date(ev.StartTime).toLocaleTimeString([], {hour:'2-digit', minute:'2-digit'})} - {new Date(ev.EndTime).toLocaleTimeString([], {hour:'2-digit', minute:'2-digit'})}</div>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    );
  };

  const renderDay = () => {
    const hours = Array.from({length:24},(_,i)=>i);
    const dayStart = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate());
    const dayEnd = new Date(dayStart.getFullYear(), dayStart.getMonth(), dayStart.getDate());
    const occurrences = parsedEvents.flatMap(pe => occurrencesForRange(pe, dayStart, dayEnd));
    const eventsByHour: Record<number, any[]> = {};
    occurrences.forEach(o => {
      const h = new Date(o.StartTime).getHours();
      if (!eventsByHour[h]) eventsByHour[h] = [];
      eventsByHour[h].push(o);
    });

    return (
      <div style={{display:'grid', gridTemplateColumns: '80px 1fr'}}>
        <div style={{display:'grid', gridAutoRows: '40px'}}>
          {hours.map(h => (
            <div key={h} className="border-b border-gray-200 dark:border-gray-700 text-gray-800 dark:text-gray-400" style={{height:40, padding:'6px 8px', fontSize:12}}>{String(h).padStart(2,'0')}:00</div>
          ))}
        </div>
        <div style={{display:'grid', gridAutoRows: '40px'}}>
          {hours.map(h => (
            <div key={h} className="border-b border-gray-200 dark:border-gray-700" style={{minHeight:40, padding:8}}>
              {(eventsByHour[h]||[]).map((ev:any,i:number)=>{
                const isFirst = ev.isFirst;
                const isDelivered = (ev.Status || ev.status || ev.original?.Status || ev.original?.status || '').toString().toUpperCase() === 'DELIVERED';
                return (
                  <div
                    key={i}
                    role="button"
                    tabIndex={0}
                    onKeyDown={() => {}}
                    onClick={() => setSelectedEvent(ev)}
                    style={{
                      cursor:'pointer', 
                      background: isDelivered ? '#28a745' : (isFirst ? '#ff751f' : '#fff3e0'), 
                      color: (isDelivered || isFirst) ? '#fff' : 'inherit',
                      padding:6, 
                      borderRadius:4, 
                      marginBottom:6
                    }}
                  >
                    <div style={{fontWeight:600}}>{renderSubject(ev.Subject || ev.subject)}</div>
                    <div style={{fontSize:12, color: (isDelivered || isFirst) ? '#eee' : '646464ff'}}>{new Date(ev.StartTime).toLocaleTimeString([], {hour:'2-digit', minute:'2-digit'})} - {new Date(ev.EndTime).toLocaleTimeString([], {hour:'2-digit', minute:'2-digit'})}</div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderContent = () => {
    if (view === 'Month') return renderMonth();
    if (view === 'Week') return renderWeek();
    return renderDay();
  };

  const closeModal = () => setSelectedEvent(null);

  const renderModal = () => {
    if (!selectedEvent) return null;

    const ev = selectedEvent;
    const fld = (k: string) => {
      const v = ev[k] ?? ev[k.toLowerCase()];
      if (v === null || v === undefined || v === '') return '-';
      return String(v);
    };

    const c = (light: string, dark: string) => isExportingTicket ? light : `${light} ${dark}`;

    return (
      <div style={{position:'fixed', inset:0, background:'rgba(0,0,0,0.4)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:9999}} onClick={closeModal}>
        <div className={c('bg-white', 'dark:bg-[#20232A] dark:text-gray-100')} style={{borderRadius:8, width: '95%', maxWidth:'500px', maxHeight:'90%', overflow:'auto'}} onClick={(e)=>e.stopPropagation()}>
          
          <div id="ticket-modal-content" className={c('bg-white text-black', 'dark:bg-[#20232A] dark:text-gray-100')} style={{padding: 20}}>
            <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8, gap: 12}}>
              <div style={{display: 'flex', alignItems: 'center', gap: 10}}>
                <div style={{fontWeight:700}}>Detalhes do Agendamento</div>
                {String(ev.Status || ev.status || ev.original?.Status || ev.original?.status || '').toUpperCase() === 'DELIVERED' ? (
                  <div style={{background: '#28a745', color: '#fff', fontSize: 11, padding: '2px 8px', borderRadius: 12, fontWeight: 700}}>
                    LIMPEZA ENTREGUE
                  </div>
                ) : ev.isFirst && (
                  <div style={{background: '#ff751f', color: '#fff', fontSize: 11, padding: '2px 8px', borderRadius: 12, fontWeight: 700}}>
                    Primeira limpeza
                  </div>
                )}
                <button 
                  type="button"
                  data-ticket-ignore="true"
                  onClick={() => setShowValue(!showValue)} 
                  className={c('text-[#646464]', 'dark:text-gray-400')}
                  style={{border:'none', background:'transparent', cursor:'pointer', display:'flex', alignItems:'center', padding:0}}
                  title={showValue ? "Ocultar Informações" : "Mostrar Informações"}
                >
                  {showValue ? React.createElement(AiOutlineEyeInvisible as any, { size: 20 }) : React.createElement(AiOutlineEye as any, { size: 20 })}
                </button>
              </div>
            </div>

          <div className={c('bg-[#f6f9ff]', 'dark:bg-[#33373E]')} style={{marginBottom: 15, padding: 14, borderRadius: 8}}>
            <div className={c('text-[#222]', 'dark:text-gray-50')} style={{fontSize: isExportingTicket ? 26 : 16, fontWeight:700, marginBottom:8}}>{fld('name')} {fld('surname')}</div>
            <div className={c('text-[#333]', 'dark:text-gray-200')} style={{fontSize: isExportingTicket ? 22 : 13, marginBottom: 6, lineHeight: 1.4}}>
              {fld('street')}, {fld('number')} {fld('complement') !== '-' ? `- ${fld('complement')}` : ''} {fld('apartment') !== '-' ? `- Apto ${fld('apartment')}` : ''} {fld('block') !== '-' ? `- Bloco ${fld('block')}` : ''}
            </div>
            <div className={c('text-[#646464]', 'dark:text-gray-400')} style={{fontSize: isExportingTicket ? 20 : 13}}>{fld('neighborhood')} • {fld('city')} / {fld('uf')} • CEP {fld('zipcode')}</div>
          </div>

          {fld('plan') === 'STUDIO' ?
          (<div style={{display:'flex', gap:12, marginBottom:12, alignItems:'center'}}>
            <div className={c('text-[#646464]', 'dark:text-gray-400')} style={{fontSize: isExportingTicket ? 20 : 13}}>Quarto:</div>
            <div style={{fontSize: isExportingTicket ? 22 : 13, fontWeight:600, marginRight:16}}>{1}</div>
            <div className={c('text-[#646464]', 'dark:text-gray-400')} style={{fontSize: isExportingTicket ? 20 : 13}}>Banheiro:</div>
            <div style={{fontSize: isExportingTicket ? 22 : 13, fontWeight:600, marginRight:16}}>{1}</div>
          </div>) :
          (<div style={{display:'flex', gap:10, marginBottom:12, alignItems:'center'}}>
            <div className={c('text-[#646464]', 'dark:text-gray-400')} style={{fontSize: isExportingTicket ? 20 : 13}}>Quartos:</div>
            <div style={{fontSize: isExportingTicket ? 22 : 13, fontWeight:600, marginRight:16}}>{fld('bedrooms')}</div>
            <div className={c('text-[#646464]', 'dark:text-gray-400')} style={{fontSize: isExportingTicket ? 20 : 13}}>Banheiros:</div>
            <div style={{fontSize: isExportingTicket ? 22 : 13, fontWeight:600, marginRight:16}}>{fld('bathrooms')}</div>
            <div className={c('text-[#646464]', 'dark:text-gray-400')} style={{fontSize: isExportingTicket ? 20 : 13}}>Lavabo:</div>
            <div style={{fontSize: isExportingTicket ? 22 : 13, fontWeight:600}}>{fld('middle')}</div>
          </div>)
          }

          <div style={{display:'flex', gap:15, marginBottom:15}}>
            {showValue && (
              <div data-ticket-ignore="true" className={c('bg-white border-[#eee]', 'dark:bg-[#2C3038] dark:border-gray-700')} style={{flex:1, padding:14, borderRadius:8, borderStyle:'solid', borderWidth: 1}}>
                <div className={c('text-[#646464]', 'dark:text-gray-400')} style={{fontSize: isExportingTicket ? 20 : 12}}>Contato</div>
                <div style={{fontSize: isExportingTicket ? 22 : 13, fontWeight:600}}>{fld('cellphone')}</div>
                <div className={c('text-[#646464]', 'dark:text-gray-400')} style={{fontSize: isExportingTicket ? 20 : 12, marginTop:10}}>E-mail</div>
                <div style={{fontSize: isExportingTicket ? 22 : 13, fontWeight:600}}>{fld('email')}</div>
              </div>
            )}

            <div className={c('bg-white border-[#eee]', 'dark:bg-[#2C3038] dark:border-gray-700')} style={{flex:1, padding:14, borderRadius:8, borderStyle:'solid', borderWidth: 1}}>
              <div className={c('text-[#646464]', 'dark:text-gray-400')} style={{fontSize: isExportingTicket ? 20 : 12}}>Serviço</div>
              <div style={{fontSize: isExportingTicket ? 22 : 13, fontWeight:700}}>{fld('plan')}</div>
              <div className={c('text-[#646464]', 'dark:text-gray-400')} style={{fontSize: isExportingTicket ? 20 : 12, marginTop:10}}>Frequência</div>
              <div style={{fontSize: isExportingTicket ? 22 : 13, fontWeight:600}}>{fld('often')}</div>
              {showValue && (
                <div data-ticket-ignore="true">
                  <div className={c('text-[#646464]', 'dark:text-gray-400')} style={{fontSize: isExportingTicket ? 20 : 12, marginTop:10}}>Valor</div>
                  <div style={{fontSize: isExportingTicket ? 22 : 13, fontWeight:700}}>{fld('value')}</div>
                  <div className={c('text-[#646464]', 'dark:text-gray-400')} style={{fontSize: isExportingTicket ? 20 : 12, marginTop:10}}>Pagamento</div>
                  <div style={{fontSize: isExportingTicket ? 22 : 13, fontWeight:600}}>{_convertPlan(fld('payment'))}</div>
                </div>
              )}
            </div>
          </div>

          <div className={c('bg-white border-[#eee]', 'dark:bg-[#2C3038] dark:border-gray-700')} style={{marginBottom:15, padding: 14, borderRadius: 8, borderStyle:'solid', borderWidth: 1}}>
            <div className={c('text-[#646464]', 'dark:text-gray-400')} style={{fontSize: isExportingTicket ? 20 : 12}}>Agendamento</div>
            <div style={{fontSize: isExportingTicket ? 22 : 13, fontWeight:600}}>{ev.StartTime ? new Date(ev.StartTime).toLocaleDateString('pt-BR') : '-'} • {fld('hour')}</div>
          </div>

          {!isExportingTicket && (
            <div data-ticket-ignore="true" className={c('bg-blue-50 border-blue-200', 'dark:bg-[#1E3A8A] dark:border-blue-800')} style={{marginBottom:15, padding: 14, borderRadius: 8, borderStyle:'solid', borderWidth: 1}}>
              <div className={c('text-blue-800', 'dark:text-blue-300')} style={{fontSize: 12, fontWeight: 700, marginBottom: 8}}>Equipe Responsável</div>
              {(() => {
                const occurrenceDate = ev.StartTime ? formatKey(new Date(ev.StartTime)) : null;
                const comboId = ev.Id || ev.id;
                const currentTeam = occurrenceDate ? TeamStore().getTeamByComboAndDate(comboId, occurrenceDate) : null;
                
                const handleAssign = (e: any) => {
                  const selectedTeamId = e.target.value;
                  if (!selectedTeamId || !occurrenceDate) return;
                  TeamStore().assignTeam(comboId, occurrenceDate, parseInt(selectedTeamId));
                };

                return (
                  <select 
                    className={c('w-full border-blue-200 rounded p-2 text-sm bg-white text-gray-800', 'dark:bg-[#2C3038] dark:border-gray-600 dark:text-gray-200')}
                    value={currentTeam?.teamId || ''}
                    onChange={handleAssign}
                  >
                    <option value="">Selecione uma equipe</option>
                    {TeamStore().teams.map(t => (
                      <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                  </select>
                );
              })()}
            </div>
          )}

          <div className={c('text-[#333]', 'dark:text-gray-200')} style={{fontSize: isExportingTicket ? 22 : 13, marginTop: 15}}>
            <div className={c('border-[#eee]', 'dark:border-gray-700')} style={{fontSize: isExportingTicket ? 22 : 13, fontWeight:600, marginBottom:12, borderBottomStyle:'solid', borderBottomWidth: 1, paddingBottom: 6}}>Informações do Serviço</div>
            
            {/* Acesso ao Imóvel */}
            <div style={{marginBottom: 14}}>
              <div className={c('text-[#646464]', 'dark:text-gray-400')} style={{fontSize: isExportingTicket ? 20 : 12, fontWeight: 700, marginBottom: 6}}>Acesso ao Imóvel</div>
              <div style={{display: 'flex', flexWrap: 'wrap', gap: 10}}>
                {ev.userPresent && <span style={{...styles.badge, fontSize: isExportingTicket ? '22px' : '13px'}}>Estarei presente para receber a equipe.</span>}
                {ev.leaveKey && <span style={{...styles.badge, fontSize: isExportingTicket ? '22px' : '13px'}}>Deixarei a chave na portaria/com o zelador.</span>}
                {ev.electronicLock && <span style={{...styles.badge, fontSize: isExportingTicket ? '22px' : '13px'}}>Possuo fechadura eletrônica {ev.code ? `(Cód: ${ev.code})` : ''}</span>}
              </div>
            </div>

            {/* Sobre o Interno/Pet */}
            <div style={{marginBottom: 14}}>
              <div className={c('text-[#646464]', 'dark:text-gray-400')} style={{fontSize: isExportingTicket ? 20 : 12, fontWeight: 700, marginBottom: 6}}>Sobre o Interno/Pet</div>
              <div style={{display: 'flex', flexWrap: 'wrap', gap: 10}}>
                {ev.noPets && <span style={{...styles.badge, fontSize: isExportingTicket ? '22px' : '13px'}}>Não tenho pets.</span>}
                {ev.freePets && <span style={{...styles.badge, fontSize: isExportingTicket ? '22px' : '13px'}}>Tenho Pet(s) e eles estarão soltos.</span>}
                {ev.trappedPets && <span style={{...styles.badge, fontSize: isExportingTicket ? '22px' : '13px'}}>Tenho Pet(s) e eles ficarão em ambiente reservado.</span>}
              </div>
            </div>

            {/* Limpeza Adicional */}
            {ev.internalCleaning && (
              <div style={{marginBottom: 14}}>
                <div className={c('text-[#646464]', 'dark:text-gray-400')} style={{fontSize: isExportingTicket ? 20 : 12, fontWeight: 700, marginBottom: 6}}>Serviços Adicionais</div>
                <div style={{...styles.badge, fontSize: isExportingTicket ? '22px' : '13px'}}>Limpeza de vidros de sacada e interior de eletrodomésticos</div>
              </div>
            )}

            {/* Sensibilidade e alergia e Itens Delicados */}
            <div style={{display: 'flex', gap: 15, marginBottom: 14}}>
              <div style={{flex: 1}}>
                <div className={c('text-[#646464]', 'dark:text-gray-400')} style={{fontSize: isExportingTicket ? 20 : 12, fontWeight: 700, marginBottom: 6}}>Sensibilidade e alergia</div>
                <div style={{...styles.badge, fontSize: isExportingTicket ? '22px' : '13px'}}>{ev.allergic ? 'Sou alérgico' : 'Sem restrições'}</div>
              </div>
              {(ev.worksOfArt || ev.plants) && (
                <div style={{flex: 1}}>
                  <div className={c('text-[#646464]', 'dark:text-gray-400')} style={{fontSize: isExportingTicket ? 20 : 12, fontWeight: 700, marginBottom: 6}}>Itens Delicados</div>
                  <div style={{display: 'flex', flexWrap: 'wrap', gap: 4}}>
                    {ev.worksOfArt && <span style={{...styles.badgeSmall, fontSize: isExportingTicket ? '18px' : '11px'}}>Possuo itens de colecionador ou obras de arte.</span>}
                    {ev.plants && <span style={{...styles.badgeSmall, fontSize: isExportingTicket ? '18px' : '11px'}}>Possuo plantas que precisam de uma borrifada de água.</span>}
                  </div>
                </div>
              )}
            </div>

            {/* Itens do Plano */}
            {getPlanDetails(fld('plan')) && (
              <div className={c('bg-[#f8f9fa] border-[#e9ecef]', 'dark:bg-[#33373E] dark:border-gray-700')} style={{marginTop: 15, padding: 12, borderRadius: 8, borderStyle:'solid', borderWidth: 1}}>
                <div className={c('text-[#495057]', 'dark:text-gray-200')} style={{fontSize: isExportingTicket ? 20 : 12, fontWeight: 700, marginBottom: 12, textTransform: 'uppercase', letterSpacing: '0.5px'}}>
                  Itens Inclusos no Plano {fld('plan')}
                </div>
                <ul className={c('text-[#444]', 'dark:text-gray-300')} style={{margin: 0, paddingLeft: 22, fontSize: isExportingTicket ? 22 : 13}}>
                  {getPlanDetails(fld('plan'))?.items.map((item, idx) => (
                    <li key={idx} style={{marginBottom: 6}}>{item}</li>
                  ))}
                </ul>
                {getPlanDetails(fld('plan'))?.time && (
                  <div style={{marginTop: 10, fontSize: isExportingTicket ? 20 : 12, fontWeight: 700, color: '#ff751f'}}>
                    ⏱ Tempo estimado: {getPlanDetails(fld('plan'))?.time}
                  </div>
                )}
              </div>
            )}

            <div style={{fontSize: isExportingTicket ? 20 : 13, fontWeight:600, marginBottom:8, marginTop: 16}}>Observações Adicionais</div>
            <div className={c('bg-[#f9f9f9] text-[#444]', 'dark:bg-[#33373E] dark:text-gray-300')} style={{whiteSpace:'pre-wrap', fontSize: isExportingTicket ? 22 : 13, lineHeight: 1.5, padding: 12, borderRadius: 6}}>{fld('ps')}</div>

            {!isExportingTicket && (() => {
              const occurrenceDate = ev.StartTime ? formatKey(new Date(ev.StartTime)) : null;
              const comboRatings = RatingStore().getRatingsByComboId(ev.Id || ev.id);
              if (!comboRatings || comboRatings.length === 0) return null;

              const matchingRating = occurrenceDate
                ? comboRatings.find(r => r.cleaningDate === occurrenceDate)
                : comboRatings[0];

              if (!matchingRating) return null;

              return (
                <div data-ticket-ignore="true" className={c('bg-[#fffbeb] border-[#fde68a]', 'dark:bg-[#33373E] dark:border-gray-700')} style={{marginTop: 16, padding: 14, borderRadius: 8, borderStyle:'solid', borderWidth: 1}}>
                  <div className={c('text-[#92400e]', 'dark:text-yellow-300')} style={{fontSize: isExportingTicket ? 20 : 12, fontWeight: 700, marginBottom: 8}}>
                    Avaliação do Cliente
                  </div>
                  {matchingRating.cleaningDate && (
                    <div className={c('text-[#92400e]', 'dark:text-yellow-200')} style={{fontSize: isExportingTicket ? 18 : 11, marginBottom: 4}}>
                      Limpeza do dia {matchingRating.cleaningDate}
                      <span style={{ margin: '0 6px' }}>•</span>
                      <span style={{ fontWeight: matchingRating.teamName ? 600 : 'normal', fontStyle: matchingRating.teamName ? 'normal' : 'italic' }}>
                        {matchingRating.teamName || 'Sem equipe'}
                      </span>
                    </div>
                  )}
                  <div style={{display: 'flex', alignItems: 'center', gap: 8, marginBottom: matchingRating.feedback ? 4 : 0}}>
                    <span style={{fontSize: isExportingTicket ? 26 : 20, color: '#F8BB03', letterSpacing: 2}}>{'★'.repeat(matchingRating.rate)}{'☆'.repeat(5 - matchingRating.rate)}</span>
                    <span className={c('text-[#92400e]', 'dark:text-yellow-200')} style={{fontSize: isExportingTicket ? 22 : 13, fontWeight: 600}}>{matchingRating.rate}/5</span>
                  </div>
                  {matchingRating.feedback && (
                    <div className={c('text-[#78350f]', 'dark:text-gray-300')} style={{fontSize: isExportingTicket ? 22 : 13, fontStyle: 'italic', lineHeight: 1.5}}>
                      "{matchingRating.feedback}"
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        </div>
        {/* Fim absoluto da div id=ticket-modal-content delimitadora */}

          <div style={{padding: '0 20px 10px', marginTop: '15px', display: 'flex', justifyContent: 'center'}}>
            <button 
              onClick={handleDownloadTicket}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                padding: '6px 14px',
                background: '#e6f4ea',
                color: '#28a745',
                border: '1px solid #c3e6cb',
                borderRadius: 6,
                fontWeight: 600,
                fontSize: 12,
                cursor: 'pointer',
                boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
              }}
            >
              {React.createElement(AiOutlineDownload as any, { size: 16 })} Baixar Ticket PNG
            </button>
          </div>

          <div style={{padding: '0 20px'}}>
            <div className="bg-[#f8f9fa] dark:bg-[#33373E] border-[#e9ecef] dark:border-gray-700" style={{marginTop: 5, padding: 12, borderRadius: 8, borderStyle:'solid', borderWidth:1}}>
              <div className="text-[#495057] dark:text-gray-200" style={{fontWeight: 700, marginBottom: 12, fontSize: 13, display: 'flex', alignItems: 'center', gap: 6}}>
                {React.createElement(AiOutlineNotification as any, { size: 16 })}
                Notificar o Usuário
              </div>
              <div style={{marginBottom: 10}}>
                <label className="text-[#646464] dark:text-gray-400" style={{display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4}}>Título</label>
                <input 
                  type="text" 
                  value={notifyTitle} 
                  onChange={(e) => setNotifyTitle(e.target.value)}
                  placeholder="Título da notificação"
                  className="bg-white dark:bg-[#20232A] text-[#333] dark:text-gray-200 border-[#ced4da] dark:border-gray-600 focus:outline-none"
                  style={{width: '100%', padding: '8px 12px', borderRadius: 6, borderStyle:'solid', borderWidth:1, fontSize: 13}}
                />
              </div>
              <div style={{marginBottom: 0}}>
                <label className="text-[#646464] dark:text-gray-400" style={{display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4}}>Mensagem</label>
                <textarea 
                  rows={3}
                  value={notifyMsg} 
                  onChange={(e) => setNotifyMsg(e.target.value)}
                  placeholder="Mensagem da notificação"
                  className="bg-white dark:bg-[#20232A] text-[#333] dark:text-gray-200 border-[#ced4da] dark:border-gray-600 focus:outline-none"
                  style={{width: '100%', padding: '8px 12px', borderRadius: 6, borderStyle:'solid', borderWidth:1, fontSize: 13, resize: 'vertical', fontFamily: 'inherit'}}
                />
              </div>

              <div style={{marginTop: 15, display: 'flex', justifyContent: 'flex-start'}}>
                <button
                  onClick={() => handleSendNotification(ev)}
                  disabled={loadingNotify || !notifyTitle.trim() || !notifyMsg.trim()}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '8px 20px',
                    background: (loadingNotify || !notifyTitle.trim() || !notifyMsg.trim()) ? '#ccc' : '#ff751f',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 6,
                    fontWeight: 700,
                    fontSize: 13,
                    cursor: (loadingNotify || !notifyTitle.trim() || !notifyMsg.trim()) ? 'not-allowed' : 'pointer',
                    opacity: loadingNotify ? 0.7 : 1,
                    transition: 'background 0.2s'
                  }}
                >
                  {loadingNotify ? 'Enviando...' : 'Enviar Notificação'}
                </button>
              </div>
            </div>
          </div>

          <div style={{padding: '20px', display: 'flex', justifyContent: 'center'}}>
            <button onClick={closeModal} className="bg-[#e0e0e0] dark:bg-[#3E434D] text-[#333] dark:text-white" style={{
                padding: '10px 40px',
                border: 'none',
                borderRadius: 6,
                fontWeight: 700,
                cursor: 'pointer',
              }}>
              Fechar
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div style={{height: height || 'auto', overflow: 'auto'}}>
      {renderHeader()}
      <div style={{padding:12}}>
        {renderContent()}
        {renderModal()}
      </div>
    </div>
  );
}));

export default ScheduleComponent;

const styles: Record<string, React.CSSProperties> = {
  badge: {
    background: '#f0f4f8',
    color: '#334e68',
    fontSize: '18px',
    padding: '6px 12px',
    borderRadius: '6px',
    fontWeight: 600,
    border: '1px solid #d9e2ec'
  },
  badgeSmall: {
    background: '#fff9db',
    color: '#856404',
    fontSize: '16px',
    padding: '5px 10px',
    borderRadius: '6px',
    fontWeight: 600,
    border: '1px solid #ffeeba'
  }
};
